// Presentation rules for the private Coach chat attached to one media activity.
//
// Deliberately pure: the component owns transport and state, these helpers own
// what a member actually sees. Mirrors the in-exercise Coach contract — the
// latest persisted turn is the focused treatment, everything older hides behind
// an explicit "Show previous messages (N)" disclosure.

export const DEFAULT_MEDIA_CHAT_EXPANDED = false;

const PENDING_RANK = { failed: 3, generating: 2, queued: 1 };

const PENDING_LABEL = {
  'analysis_note:queued': 'Coach is reviewing your new photos…',
  'analysis_note:generating': 'Coach is writing about your photos…',
  'reply:queued': 'Coach will answer in a moment…',
  'reply:generating': 'Coach is typing…',
  'analysis_note:failed': 'Coach could not finish the note about your photos.',
  'reply:failed': 'Coach could not finish that reply.',
};

export function uniqueMediaCoachMessages(messages = []) {
  const seen = new Set();
  const unique = [];
  (Array.isArray(messages) ? messages : []).forEach((message, index) => {
    const key = message?._id ? String(message._id) : `index:${index}`;
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(message);
  });
  return unique;
}

export function visibleMediaCoachMessages({ messages = [], expanded = DEFAULT_MEDIA_CHAT_EXPANDED } = {}) {
  const unique = uniqueMediaCoachMessages(messages);
  if (expanded) return unique;
  return unique.length > 0 ? unique.slice(-1) : [];
}

export function hiddenMediaCoachMessageCount(messages = []) {
  return Math.max(0, uniqueMediaCoachMessages(messages).length - 1);
}

/**
 * Two different media activities can hold equal-length threads, so the scroll
 * identity has to include the activity. A generation cue changes the rendered
 * height too, so it participates as well.
 */
export function mediaCoachHistoryKey({ activityId, count = 0, pendingKey = 'none' } = {}) {
  return `${String(activityId ?? 'unknown')}|${Number(count) || 0}|${String(pendingKey || 'none')}`;
}

export function historyScrollTop(element) {
  if (!element) return 0;
  return Math.max(0, (Number(element.scrollHeight) || 0) - (Number(element.clientHeight) || 0));
}

export function mediaCoachPendingPresentation(pending = []) {
  const candidates = (Array.isArray(pending) ? pending : []).filter(entry => PENDING_RANK[entry?.state]);
  if (candidates.length === 0) return null;
  const winner = candidates.reduce((best, entry) => (
    PENDING_RANK[entry.state] > PENDING_RANK[best.state] ? entry : best
  ));
  const kind = winner.kind === 'reply' ? 'reply' : 'analysis_note';
  return {
    requestId: winner._id ? String(winner._id) : null,
    kind,
    state: winner.state,
    retryable: winner.state === 'failed' && winner.retryable !== false,
    selectedExternal: winner.retryable === false || Boolean(winner.external_task_id),
    errorCode: winner.error_code || null,
    label: PENDING_LABEL[`${kind}:${winner.state}`] || 'Coach is working on this.',
  };
}

export function mediaCoachMessageKey(message, index) {
  return message?._id ? String(message._id) : `${message?.role || 'turn'}-${index}`;
}
